import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import RBACApiService from '../../services/rbacApiService';

const PermissionManagement = () => {
    const [permissions, setPermissions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadPermissions();
    }, []);

    const loadPermissions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await RBACApiService.getPermissions();
            // Handle API response structure
            let permissionsData = [];
            if (response && response.success && response.data) {
                permissionsData = response.data;
            } else if (Array.isArray(response)) {
                permissionsData = response;
            } else {
                console.warn('Unexpected permissions response format:', response);
            }

            setPermissions(permissionsData);

            // Extract unique categories
            const uniqueCategories = [...new Set(permissionsData.map((p) => p.category))];
            setCategories(uniqueCategories);
        } catch (error) {
            console.error('Error loading permissions:', error);
            setError('Failed to load permissions');
            setPermissions([]);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const createPermission = async (permissionData) => {
        try {
            await RBACApiService.createPermission(permissionData);
            loadPermissions();
        } catch (error) {
            console.error('Error creating permission:', error);
            setError('Failed to create permission');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Permission Management
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {categories.map((category) => (
                    <Grid item xs={12} md={6} key={category}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {category.replace(/_/g, ' ').toUpperCase()}
                                </Typography>

                                {permissions
                                    .filter((p) => p.category === category)
                                    .map((permission) => (
                                        <Box key={permission.id} sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {permission.resource}:{permission.action}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {permission.description}
                                            </Typography>
                                            <Chip
                                                label={permission.is_system_permission ? 'System' : 'Custom'}
                                                size="small"
                                                color={permission.is_system_permission ? 'primary' : 'secondary'}
                                                sx={{ mt: 1 }}
                                            />
                                        </Box>
                                    ))}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Permission Categories Accordion */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Permission Categories
                </Typography>
                {categories.map((category) => (
                    <Accordion key={category}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">{category.replace(/_/g, ' ').toUpperCase()}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {permissions
                                    .filter((p) => p.category === category)
                                    .map((permission) => (
                                        <Grid item xs={12} sm={6} md={4} key={permission.id}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                        {permission.resource}:{permission.action}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {permission.description}
                                                    </Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        <Chip
                                                            label={permission.is_system_permission ? 'System' : 'Custom'}
                                                            size="small"
                                                            color={permission.is_system_permission ? 'primary' : 'secondary'}
                                                        />
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </Box>
    );
};

export default PermissionManagement;
